import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { WorkspaceMember } from '@molt/shared';
import { Avatar, Button, Dialog, Notice, profileName } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface WorkspaceMemberRemoveDialogProps {
  member: WorkspaceMember | null;
  busy?: boolean;
  error?: string | null;
  onConfirm: (removeFromBoards: boolean) => void;
  onCancel: () => void;
}

export function WorkspaceMemberRemoveDialog({
  member,
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: WorkspaceMemberRemoveDialogProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [removeFromBoards, setRemoveFromBoards] = useState(false);
  const name =
    member === null
      ? t('workspaceSettings.removeMember.fallbackName')
      : profileName(member);

  useEffect(() => {
    if (member === null) setRemoveFromBoards(false);
  }, [member]);

  return (
    <Dialog
      open={member !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title={t('workspaceSettings.removeMember.title', { name })}
      description={t('workspaceSettings.removeMember.description')}
      size="sm"
      initialFocus={cancelRef}
      footer={
        <>
          <Button
            variant="danger"
            busy={busy}
            onClick={() => onConfirm(removeFromBoards)}
          >
            {t('workspaceSettings.removeMember.confirm')}
          </Button>
          <Button ref={cancelRef} variant="quiet" onClick={onCancel}>
            {t('workspaceSettings.removeMember.cancel')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null ? <Notice tone="bad" body={error} /> : null}
        {member !== null ? (
          <div className="flex items-center gap-3 rounded-card border border-border-strong bg-surface-raised p-3">
            <Avatar profile={member} size="sm" decorative />
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{name}</p>
              <p className="text-sm text-ink-muted">
                {member.role === 'admin'
                  ? t('workspaceSettings.removeMember.role.admin')
                  : t('workspaceSettings.removeMember.role.normal')}
              </p>
            </div>
          </div>
        ) : null}
        <Notice tone="warn" body={t('workspaceSettings.removeMember.lastAdminWarning')} />
        <label className="flex gap-2 rounded-card border border-border-strong bg-surface-raised p-3 text-sm text-ink">
          <input
            type="radio"
            name="workspace-remove-scope"
            checked={!removeFromBoards}
            onChange={() => setRemoveFromBoards(false)}
          />
          <span>{t('workspaceSettings.removeMember.scopeWorkspaceOnly', { name })}</span>
        </label>
        <label className="flex gap-2 rounded-card border border-border-strong bg-surface-raised p-3 text-sm text-ink">
          <input
            type="radio"
            name="workspace-remove-scope"
            checked={removeFromBoards}
            onChange={() => setRemoveFromBoards(true)}
          />
          <span>{t('workspaceSettings.removeMember.scopeAllBoards')}</span>
        </label>
      </div>
    </Dialog>
  );
}
